// Verificación normativa inicial (PE-A1). Capa nacional (AM 1257) siempre (PE-3); capa de Quito
// (RTQ 2026) si el cantón es Quito (matriz 9.3). En cada regla gana el requisito más exigente.

import {
  BRIGADA_NACIONAL, BRIGADA_QUITO_INDUSTRIAL, EXTINTOR_DISTANCIA_NACIONAL, EXTINTOR_DISTANCIA_QUITO,
  HIDROSTATICA_ANOS, ILUMINACION_NACIONAL, ILUMINACION_QUITO, PLAN_OBLIGATORIO,
} from './pe-constantes-normativa';
import { Traza, unirFuentes } from './pe-tipos';
import { numEc } from './pe-unidades';

const METODO = 'verificacion';

export function esQuito(canton: string): boolean {
  const c = canton.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
  return c === 'quito' || c === 'distrito metropolitano de quito' || c === 'dmq';
}

export interface ContextoVerificacion {
  canton: string;
  ocupacion: 'industrial' | 'almacenamiento' | 'oficinas' | 'otra';
  trabajadores: number;
  tieneBrigada?: boolean;
  distanciaMaxExtintorM?: number;
  iluminacion?: { duracionMin?: number; luxPromedio?: number; luxMinimo?: number; separacionMaxM?: number };
  hidrostatica?: { anosDesdeUltimaPrueba: number; intervaloAnos?: number };
}

const estado = (ok: boolean): 'cumple' | 'no_cumple' => (ok ? 'cumple' : 'no_cumple');

export function verificar(ctx: ContextoVerificacion): Traza[] {
  const quito = esQuito(ctx.canton);
  const trazas: Traza[] = [];

  // Plan de emergencia: siempre obligatorio (PE-4).
  trazas.push({
    metodo: METODO, norma: PLAN_OBLIGATORIO.norma, articulo_o_tabla: PLAN_OBLIGATORIO.articulo_o_tabla,
    umbral: 'obligatorio', valor: 'obligatorio', unidad: '—', estado: 'informativo', fuente_doc: PLAN_OBLIGATORIO.fuente_doc,
    nota: 'El plan es obligatorio en toda empresa',
  });

  // Extintor: 20 m nacional; 15 m en Quito (gana el menor).
  if (ctx.distanciaMaxExtintorM !== undefined) {
    const regla = quito ? EXTINTOR_DISTANCIA_QUITO : EXTINTOR_DISTANCIA_NACIONAL;
    trazas.push({
      metodo: METODO, norma: regla.norma, articulo_o_tabla: regla.articulo_o_tabla, umbral: regla.valor,
      valor: ctx.distanciaMaxExtintorM, unidad: 'm', estado: estado(ctx.distanciaMaxExtintorM <= regla.valor),
      fuente_doc: regla.fuente_doc,
      nota: quito ? `Quito: 15 m (RTQ 7) es más exigente que 20 m (AM 1257 Art. 120)` : 'Mínimo nacional',
    });
  }

  // Brigada: > 20 trabajadores (AM 1257 Art. 169); en Quito industrial, > 25 (RTQ 3). Obligatoria si alguna la exige.
  const porNacional = ctx.trabajadores > BRIGADA_NACIONAL.valor;
  const aplicaQuito = quito && ctx.ocupacion === 'industrial';
  const porQuito = aplicaQuito && ctx.trabajadores > BRIGADA_QUITO_INDUSTRIAL.valor;
  const obligatoria = porNacional || porQuito;
  const reglaBrigada = porNacional || !aplicaQuito ? BRIGADA_NACIONAL : BRIGADA_QUITO_INDUSTRIAL;
  trazas.push({
    metodo: METODO, norma: reglaBrigada.norma, articulo_o_tabla: reglaBrigada.articulo_o_tabla,
    umbral: `> ${reglaBrigada.valor}`, valor: ctx.trabajadores, unidad: 'trabajadores',
    estado: !obligatoria || ctx.tieneBrigada === undefined ? 'informativo' : estado(ctx.tieneBrigada),
    fuente_doc: aplicaQuito ? unirFuentes(BRIGADA_NACIONAL.fuente_doc, BRIGADA_QUITO_INDUSTRIAL.fuente_doc) : BRIGADA_NACIONAL.fuente_doc,
    nota: obligatoria ? 'Brigada obligatoria' : 'Brigada no obligatoria por número de trabajadores',
  });

  // Iluminación de emergencia: 60 min, 10 lux promedio, 1 lux mínimo (PE-7); en Quito, además, lámparas cada 12 m como máximo.
  const il = ctx.iluminacion;
  if (il) {
    const n = ILUMINACION_NACIONAL;
    const reglas: [number | undefined, number, string, string][] = [
      [il.duracionMin, n.duracionMin, 'min', 'Duración'],
      [il.luxPromedio, quito ? Math.max(n.luxPromedio, ILUMINACION_QUITO.luxSuelo) : n.luxPromedio, 'lux', 'Iluminancia promedio'],
      [il.luxMinimo, n.luxMinimo, 'lux', 'Iluminancia mínima'],
    ];
    for (const [valor, umbral, unidad, que] of reglas) {
      if (valor === undefined) continue;
      trazas.push({
        metodo: METODO, norma: n.norma, articulo_o_tabla: n.articulo_o_tabla, umbral, valor, unidad,
        estado: estado(valor >= umbral), fuente_doc: n.fuente_doc, nota: que,
      });
    }
    if (quito && il.separacionMaxM !== undefined) {
      trazas.push({
        metodo: METODO, norma: ILUMINACION_QUITO.norma, articulo_o_tabla: ILUMINACION_QUITO.articulo_o_tabla,
        umbral: ILUMINACION_QUITO.separacionMaxM, valor: il.separacionMaxM, unidad: 'm',
        estado: estado(il.separacionMaxM <= ILUMINACION_QUITO.separacionMaxM), fuente_doc: ILUMINACION_QUITO.fuente_doc,
        nota: 'Separación entre luces de emergencia (y en cambios de dirección)',
      });
    }
  }

  // Prueba hidrostática: cada 6 años por defecto (AM 1257 Art. 32 d), editable (PE-32).
  if (ctx.hidrostatica) {
    const intervalo = ctx.hidrostatica.intervaloAnos ?? HIDROSTATICA_ANOS.valor;
    trazas.push({
      metodo: METODO, norma: HIDROSTATICA_ANOS.norma, articulo_o_tabla: HIDROSTATICA_ANOS.articulo_o_tabla,
      umbral: intervalo, valor: ctx.hidrostatica.anosDesdeUltimaPrueba, unidad: 'años',
      estado: estado(ctx.hidrostatica.anosDesdeUltimaPrueba <= intervalo),
      fuente_doc: HIDROSTATICA_ANOS.fuente_doc,
      nota: ctx.hidrostatica.intervaloAnos === undefined ? 'AM 1257, Art. 32 d): cada 6 años' : `Intervalo editado por el responsable: ${numEc(intervalo)} años (por defecto, AM 1257, Art. 32 d): 6)`,
    });
  }

  return trazas;
}

// Brigada obligatoria según las capas que aplican (atajo para la pantalla y las pruebas).
export function brigadaObligatoria(ctx: Pick<ContextoVerificacion, 'canton' | 'ocupacion' | 'trabajadores'>): boolean {
  const porQuito = esQuito(ctx.canton) && ctx.ocupacion === 'industrial' && ctx.trabajadores > BRIGADA_QUITO_INDUSTRIAL.valor;
  return ctx.trabajadores > BRIGADA_NACIONAL.valor || porQuito;
}
